import { useEffect } from "react";

type Props = { path: string };

type SeoRoute = {
  path: string;
  title: string;
  description: string;
};

const SITE_ORIGIN = "https://juluy.cafe24.com";

const DEFAULT_SEO: SeoRoute = {
  path: "/",
  title: "주류페이 - 사업장 주류대금 신용카드로 결제하세요",
  description:
    "주류페이는 사업장 주류대금을 신용카드로 간편하게 결제하고, 결제요청/정산 상태를 한 번에 관리할 수 있는 서비스입니다.",
};

const SEO_BY_PATH: SeoRoute[] = [
  {
    path: "/",
    title: "주류페이 - 사업장 주류대금 신용카드 결제",
    description:
      "사업장 주류대금을 신용카드로 결제하고 결제 요청·승인·정산 흐름을 간편하게 관리하세요.",
  },
  {
    path: "/guide",
    title: "주류페이 이용안내 - 가입부터 결제완료까지",
    description:
      "주류페이 이용안내 페이지에서 회원가입, 결제등록, 결제완료까지의 절차를 단계별로 확인하세요.",
  },
  {
    path: "/faq",
    title: "주류페이 FAQ - 자주 묻는 질문",
    description:
      "주류대금 카드결제, 결제요청 등록, 승인/정산 관련 자주 묻는 질문을 확인하세요.",
  },
  {
    path: "/support",
    title: "주류페이 고객센터 - 문의 및 지원",
    description: "주류페이 이용 중 문의사항을 남기고 지원을 받으세요.",
  },
  {
    path: "/registration",
    title: "주류페이 결제등록 - 사업장 주류대금 등록",
    description:
      "주류페이에서 사업장 주류대금 결제요청을 등록하고 카드결제를 시작하세요.",
  },
  {
    path: "/contracts/new",
    title: "주류페이 결제등록 - 사업장 주류대금 등록",
    description:
      "주류페이에서 사업장 주류대금 결제요청을 등록하고 카드결제를 시작하세요.",
  },
];

function upsertMeta(kind: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(
    `meta[${kind}="${key}"]`
  ) as HTMLMetaElement | null;

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

export default function SeoMetaManager({ path }: Props) {
  useEffect(() => {
    const isNoindex =
      path.startsWith("/admin") ||
      path.startsWith("/pay/") ||
      path.startsWith("/pay-now") ||
      path.startsWith("/mypage") ||
      path.startsWith("/dashboard") ||
      path.startsWith("/login") ||
      path.startsWith("/signup") ||
      path.startsWith("/find-account") ||
      path.startsWith("/social-login") ||
      path.startsWith("/debug");

    const base =
      SEO_BY_PATH.find((x) => path === x.path || path.startsWith(`${x.path}/`)) ||
      DEFAULT_SEO;

    const canonicalPath =
      path === "/" ||
      path === "/guide" ||
      path === "/faq" ||
      path === "/support" ||
      path === "/registration" ||
      path.startsWith("/contracts/new")
        ? base.path
        : path;

    const canonical = `${SITE_ORIGIN}${canonicalPath}`;
    const robots = isNoindex ? "noindex,nofollow" : "index,follow";

    document.title = base.title;
    upsertMeta("name", "description", base.description);
    upsertMeta("name", "robots", robots);
    upsertMeta("property", "og:title", base.title);
    upsertMeta("property", "og:description", base.description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("name", "twitter:title", base.title);
    upsertMeta("name", "twitter:description", base.description);
    upsertCanonical(canonical);
  }, [path]);

  return null;
}
